/**
 * Where the buildings' sun shadows lie in each room, for the bodies standing in them: a body in a
 * building's shade is darkened like the ground under it, and casts no shadow of its own there (the
 * building already took the sun). Each storey's MapRenderingManager publishes its storey's shadow and
 * its strength this frame; a sprite asks about its feet. Pure: no pixi.
 */
import { insidePolygon, ShadowPolygon } from "./building_shadow_geometry"

interface RoomShade { owner: object; polygons: readonly ShadowPolygon[]; boxes: Float32Array; alpha: number }

const shades = new Map<string, RoomShade>()

/** A storey's shadow, in its room's own pixels. The latest publisher of a room owns it. */
export const publishBuildingShade = (roomId: string, owner: object, polygons: readonly ShadowPolygon[]): void => {
    const boxes = new Float32Array(polygons.length * 4)
    polygons.forEach((polygon, i) => {
        let minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity
        for (let k = 0; k < polygon.length; k += 2) {
            minX = Math.min(minX, polygon[k]); maxX = Math.max(maxX, polygon[k])
            minY = Math.min(minY, polygon[k + 1]); maxY = Math.max(maxY, polygon[k + 1])
        }
        boxes.set([minX, minY, maxX, maxY], i * 4)
    })
    shades.set(roomId, { owner, polygons, boxes, alpha: shades.get(roomId)?.alpha ?? 0 })
}

/** How strong the shadow shows this frame (0: off, night, or not drawn). */
export const setBuildingShadeAlpha = (roomId: string, alpha: number): void => {
    const shade = shades.get(roomId)
    if (shade) shade.alpha = alpha
}

export const withdrawBuildingShade = (roomId: string, owner: object): void => {
    if (shades.get(roomId)?.owner === owner) shades.delete(roomId)
}

/** How dark the buildings' shade is at a point of a room: 0 in the sun. */
export const buildingShadeAt = (roomId: string | undefined, x: number, y: number): number => {
    const shade = roomId === undefined ? undefined : shades.get(roomId)
    if (!shade || shade.alpha <= 0) return 0
    const { boxes, polygons } = shade
    for (let i = 0; i < polygons.length; i++) {
        const b = i * 4
        if (x < boxes[b] || y < boxes[b + 1] || x > boxes[b + 2] || y > boxes[b + 3]) continue
        if (insidePolygon(polygons[i], x, y)) return shade.alpha
    }
    return 0
}

/** A tint darkened by a shade, as a black shadow of that alpha would darken the colour under it. */
export const shadeTint = (tint: number, shade: number): number => {
    if (shade <= 0) return tint
    const k = 1 - shade
    const r = Math.round(((tint >> 16) & 0xff) * k), g = Math.round(((tint >> 8) & 0xff) * k), b = Math.round((tint & 0xff) * k)
    return (r << 16) | (g << 8) | b
}
